/**
 * Admin Panel - System Prompts Management API (NEX-2421)
 *
 * Self-hosted prompt store with Vercel-deployments-style version publishing.
 * Endpoints: list prompts, get one (with production), list its versions,
 * create a new version (draft), publish a version (promote / rollback).
 *
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Content / config shapes (mirror SystemPromptVersion.content/config)
// ============================================================

/** A single CHAT message (role/content), matching the runtime reader. */
export const SystemPromptChatMessageSchema = z.object({
  role: z.string(),
  content: z.string()
});

/** TEXT prompts store a string; CHAT prompts store an array of messages. */
export const SystemPromptContentSchema = z.union([
  z.string(),
  z.array(SystemPromptChatMessageSchema)
]);

/** Optional author-attached model config (modelName, temperature, …). */
export const SystemPromptConfigSchema = z.record(z.string(), z.unknown()).nullable();

// ============================================================
// View schemas
// ============================================================

/** One immutable version row, enriched for the timeline + editor/diff. */
export const SystemPromptVersionViewSchema = z.object({
  id: z.string(),
  version: z.number(),
  note: z.string().nullable(),
  config: SystemPromptConfigSchema,
  content: SystemPromptContentSchema,
  isProduction: z.boolean(),
  createdAt: z.string(),
  createdBy: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
});

/** A prompt in the list view (no content — keeps the list payload small). */
export const SystemPromptSummarySchema = z.object({
  key: z.string(),
  type: z.enum(DbEnum.SYSTEM_PROMPT_TYPE_VALUES),
  description: z.string().nullable(),
  productionVersion: z.number().nullable(),
  versionCount: z.number(),
  /** Display name of whoever authored the live production version. */
  productionAuthor: z.string().nullable(),
  updatedAt: z.string()
});

/** A prompt detail view, including its live production version. */
export const SystemPromptDetailSchema = SystemPromptSummarySchema.extend({
  productionVersionId: z.string().nullable(),
  production: SystemPromptVersionViewSchema.nullable()
});

// ============================================================
// Request bodies / path vars
// ============================================================

export const SystemPromptKeyPathSchema = z.object({
  key: z.string()
});

export const CreateSystemPromptVersionBodySchema = z.object({
  content: SystemPromptContentSchema,
  note: z.string().optional(),
  config: SystemPromptConfigSchema.optional()
});

export const PublishSystemPromptVersionBodySchema = z.object({
  versionId: z.string().uuid()
});

/**
 * A portable prompt entry (production content), shared by export and import.
 * Same shape as the boot seed snapshot, so files round-trip between environments.
 */
export const SystemPromptExportEntrySchema = z
  .object({
    key: z.string(),
    type: z.enum(DbEnum.SYSTEM_PROMPT_TYPE_VALUES),
    description: z.string().nullable(),
    content: SystemPromptContentSchema,
    config: SystemPromptConfigSchema
  })
  // Reject self-inconsistent files at the boundary: TEXT must carry a string,
  // CHAT a message array. (The import use case additionally checks content
  // against the *existing* prompt's type before publishing.)
  .refine(
    (entry) =>
      entry.type === "CHAT" ? Array.isArray(entry.content) : typeof entry.content === "string",
    { message: "content must be a string for TEXT prompts and a message array for CHAT prompts" }
  );

export const ExportSystemPromptsBodySchema = z.object({
  keys: z.array(z.string()).min(1)
});

export const ImportSystemPromptsBodySchema = z.object({
  prompts: z.array(SystemPromptExportEntrySchema).min(1)
});

export const ImportSystemPromptsResultSchema = z.object({
  imported: z.number(),
  skipped: z.array(z.object({ key: z.string(), reason: z.string() }))
});

// ============================================================
// ZAdminSystemPrompts - Endpoint Definitions
// ============================================================

export const ZAdminSystemPrompts = {
  /** List all self-hosted prompts. GET /api/admin/system-prompts */
  ListPrompts: {
    method: "GET" as const,
    path: "/api/admin/system-prompts",
    Response: ApiSuccessSchema(z.object({ prompts: z.array(SystemPromptSummarySchema) }))
  },

  /** Get one prompt + its production version. GET /api/admin/system-prompts/:key */
  GetPrompt: {
    method: "GET" as const,
    path: "/api/admin/system-prompts/:key",
    PathVars: SystemPromptKeyPathSchema,
    Response: ApiSuccessSchema(SystemPromptDetailSchema)
  },

  /** List a prompt's version history. GET /api/admin/system-prompts/:key/versions */
  ListVersions: {
    method: "GET" as const,
    path: "/api/admin/system-prompts/:key/versions",
    PathVars: SystemPromptKeyPathSchema,
    Response: ApiSuccessSchema(z.object({ versions: z.array(SystemPromptVersionViewSchema) }))
  },

  /** Save a new draft version. POST /api/admin/system-prompts/:key/versions */
  CreateVersion: {
    method: "POST" as const,
    path: "/api/admin/system-prompts/:key/versions",
    PathVars: SystemPromptKeyPathSchema,
    Body: CreateSystemPromptVersionBodySchema,
    Response: ApiSuccessSchema(SystemPromptVersionViewSchema)
  },

  /** Promote a version to production (or roll back). POST /api/admin/system-prompts/:key/publish */
  PublishVersion: {
    method: "POST" as const,
    path: "/api/admin/system-prompts/:key/publish",
    PathVars: SystemPromptKeyPathSchema,
    Body: PublishSystemPromptVersionBodySchema,
    Response: ApiSuccessSchema(SystemPromptDetailSchema)
  },

  /** Export selected prompts' production content. POST /api/admin/system-prompts/export */
  ExportPrompts: {
    method: "POST" as const,
    path: "/api/admin/system-prompts/export",
    Body: ExportSystemPromptsBodySchema,
    Response: ApiSuccessSchema(z.object({ prompts: z.array(SystemPromptExportEntrySchema) }))
  },

  /** Import prompts (new version + publish per entry). POST /api/admin/system-prompts/import */
  ImportPrompts: {
    method: "POST" as const,
    path: "/api/admin/system-prompts/import",
    Body: ImportSystemPromptsBodySchema,
    Response: ApiSuccessSchema(ImportSystemPromptsResultSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type SystemPromptChatMessage = z.infer<typeof SystemPromptChatMessageSchema>;
export type SystemPromptContent = z.infer<typeof SystemPromptContentSchema>;
export type SystemPromptVersionView = z.infer<typeof SystemPromptVersionViewSchema>;
export type SystemPromptSummary = z.infer<typeof SystemPromptSummarySchema>;
export type SystemPromptDetail = z.infer<typeof SystemPromptDetailSchema>;
export type SystemPromptExportEntry = z.infer<typeof SystemPromptExportEntrySchema>;
